package com.milta.feature.importjobs.data.remote

import com.milta.core.network.apiExceptionOf
import com.milta.core.network.Endpoints
import com.milta.core.network.baseUrl
import com.milta.feature.auth.data.remote.dto.ApiError
import com.milta.feature.importjobs.data.remote.dto.ImportJobDto
import com.milta.feature.importjobs.domain.entity.ImportJobFlow
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.onUpload
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.statement.HttpResponse
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess

/**
 * `/api/v1/import-jobs` — asynchronous AI imports. The upload only stores the file and answers 202;
 * the extraction runs server-side and is followed through [get] / [listActive].
 * Synchronous refusals still come back as an [ApiException] carrying the backend code
 * (see [com.milta.feature.importjobs.domain.entity.ImportJobErrorCodes]): 400 format/size,
 * 403 subscription/quota, 409, 429 rate limit.
 */
class ImportJobsApi(private val client: HttpClient) {

    suspend fun submit(
        flow: ImportJobFlow,
        fileBytes: ByteArray,
        fileName: String,
        routeId: String? = null,
        /** Bytes of the request body actually sent so far, and its total size. */
        onUpload: (sentBytes: Long, totalBytes: Long) -> Unit = { _, _ -> },
    ): ImportJobDto =
        client.submitFormWithBinaryData(
            url = "$baseUrl/${Endpoints.importJobSubmit(flow.pathSegment)}",
            formData = formData {
                routeId?.let { append("routeId", it) }
                append(
                    key = "file",
                    value = fileBytes,
                    headers = Headers.build {
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ) {
            onUpload { sent, total -> onUpload(sent, total ?: fileBytes.size.toLong()) }
        }.decodeOrThrow()

    /** 404 when the job does not belong to the account. */
    suspend fun get(id: String): ImportJobDto =
        client.get("$baseUrl/${Endpoints.importJob(id)}").decodeOrThrow()

    /** Pending/Processing jobs plus finished ones not yet acknowledged. */
    suspend fun listActive(): List<ImportJobDto> =
        client.get("$baseUrl/${Endpoints.importJobs}") { parameter("active", true) }.decodeOrThrow()

    /**
     * Relaunches a failed job from the file already stored server-side. [flow] must be the job's own
     * flow (404 otherwise). 409 when the stored file expired, attempts are exhausted or the job is not Failed.
     */
    suspend fun retry(flow: ImportJobFlow, id: String): ImportJobDto =
        client.post("$baseUrl/${Endpoints.importJobRetry(flow.pathSegment, id)}").decodeOrThrow()

    /** Allowed on Pending, Processing and Failed jobs — 200 with the Cancelled job. */
    suspend fun cancel(id: String): ImportJobDto =
        client.post("$baseUrl/${Endpoints.importJobCancel(id)}").decodeOrThrow()

    /** The app has consumed the result: the job leaves the active list. */
    suspend fun acknowledge(id: String) =
        client.post("$baseUrl/${Endpoints.importJobAcknowledge(id)}").throwIfFailed()

    private suspend inline fun <reified T> HttpResponse.decodeOrThrow(): T {
        throwIfFailed()
        return body()
    }

    private suspend fun HttpResponse.throwIfFailed() {
        if (!status.isSuccess()) {
            val apiError = runCatching { body<ApiError>() }.getOrNull()
            // MIL-98 — the AI credit 403 keeps its reset date.
            throw apiExceptionOf(status.value, apiError)
        }
    }
}
